import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cloud, ChevronRight, CheckCircle, Calendar as CalendarLucide, Plus, X, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';

// ====== Jours de Pluie — Panneau Latéral ======
function JoursPluieSheet({ open, onClose, bonsPluie, meteo, onRefresh, navigate, currentDate, onPluieConflict }) {
  const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
  const [planifyingId, setPlanifyingId] = useState(null);
  const [plannedIds, setPlannedIds] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(null); // bon.id or null
  const [selectedMultiDates, setSelectedMultiDates] = useState([]);
  const [pluieIndicateurs, setPluieIndicateurs] = useState([]);
  const [indLoading, setIndLoading] = useState(false);
  const [indTargetPct, setIndTargetPct] = useState(null);
  // 🆕 2026-05-18 — Chef dont la décomposition est dépliée (null = aucune)
  const [chefDeplie, setChefDeplie] = useState(null);

  useEffect(() => {
    if (open) {
      const d = currentDate || new Date();
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      setIndLoading(true);
      axios.get(`${API_URL}/api/configuration/pluie-indicateurs/${y}/${m}`)
        .then(r => r.data)
        .then(d => {
          setPluieIndicateurs(d.indicateurs || []);
          setIndTargetPct(d.target_pct);
        })
        .catch(() => {})
        .finally(() => setIndLoading(false));
    }
  }, [open, currentDate, API_URL]);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const tomorrowStr = new Date(today.getTime() + 86400000).toISOString().split('T')[0];

  // Prochains jours pluvieux dans les 7 jours
  const upcomingRainyDays = Object.entries(meteo)
    .filter(([date, f]) => date >= todayStr && (f.precip_pct >= 40 || [51,53,55,61,63,65,80,81,82].includes(f.code)))
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(0, 7);

  const weatherEmoji = (code) => {
    if (code === 0) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '🌫️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '❄️';
    if (code <= 82) return '🌧️';
    if (code >= 95) return '⛈️';
    return '☁️';
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handlePlanifier = async (bon, dateStr = null, confirmerRallongement = undefined) => {
    const key = dateStr ? `${bon.id}-${dateStr}` : `${bon.id}-plage`;
    setPlanifyingId(key);
    try {
      const body = dateStr ? { date: dateStr } : { planifier_plage: true };
      if (confirmerRallongement !== undefined) body.confirmer_rallongement = confirmerRallongement;
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/planifier-pluie`, body);
      if (res.status === 200) {
        const data = res.data;
        // 🆕 2026-05-09 — Conflit détecté : déléguer au modal du parent
        if (data.requires_confirmation && onPluieConflict) {
          onPluieConflict({
            bon,
            date: dateStr,
            btsARallonger: data.bts_a_rallonger || [],
            retry: (confirm) => handlePlanifier(bon, dateStr, confirm),
          });
          return;
        }
        if (dateStr) {
          setPlannedIds(prev => ({ ...prev, [`${bon.id}-${dateStr}`]: true }));
          toast.success(`Projet planifié pour le ${new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}`);
        } else {
          setPlannedIds(prev => ({ ...prev, [`${bon.id}-plage`]: true }));
          toast.success(`Projet planifié pour toute la plage — ${data.nb_dates || 0} jours ajoutés au calendrier`);
        }
        onRefresh();
      } else {
        const err = res.data || {};
        toast.error(err.detail || 'Erreur lors de la planification');
      }
    } catch (e) {
      const err = e.response?.data || {};
      toast.error(err.detail || 'Erreur réseau');
    } finally {
      setPlanifyingId(null);
    }
  };

  const isAlreadyPlanned = (bon, dateStr) => {
    return (bon.dates_chantier || []).includes(dateStr) || plannedIds[`${bon.id}-${dateStr}`];
  };

  const isPlageAlreadyPlanned = (bon) => {
    if (!bon.pluie_date_debut || !bon.pluie_date_fin) return false;
    if (plannedIds[`${bon.id}-plage`]) return true;
    const dates = bon.dates_chantier || [];
    if (dates.length === 0) return false;
    return dates.includes(bon.pluie_date_debut) && dates.includes(bon.pluie_date_fin);
  };

  const toggleMultiDate = (dateStr) => {
    setSelectedMultiDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort());
  };

  const handlePlanifierMultiDates = async (bon) => {
    if (selectedMultiDates.length === 0) { toast.error('Selectionnez au moins une date'); return; }
    setPlanifyingId(`${bon.id}-multi`);
    try {
      let nbDone = 0;
      for (const dateStr of selectedMultiDates) {
        const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/planifier-pluie`, {
          date: dateStr
        });
        // 🆕 2026-05-09 — Si conflit, déléguer au modal puis arrêter la boucle
        if (res.data?.requires_confirmation && onPluieConflict) {
          onPluieConflict({
            bon,
            date: dateStr,
            btsARallonger: res.data.bts_a_rallonger || [],
            retry: async (confirm) => {
              await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/planifier-pluie`, {
                date: dateStr, confirmer_rallongement: confirm,
              });
              onRefresh();
            },
          });
          break;
        }
        nbDone += 1;
      }
      if (nbDone > 0) toast.success(`${nbDone} jour(s) ajoute(s) au calendrier`);
      setSelectedMultiDates([]);
      setShowDatePicker(null);
      onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setPlanifyingId(null); }
  };

  // Générer les jours ouvrables de la plage pluie pour le sélecteur
  const getPlageJours = (bon) => {
    if (!bon.pluie_date_debut || !bon.pluie_date_fin) return [];
    const jours = [];
    let cur = new Date(bon.pluie_date_debut + 'T12:00:00');
    const end = new Date(bon.pluie_date_fin + 'T12:00:00');
    while (cur <= end) {
      if (cur.getDay() >= 1 && cur.getDay() <= 5) jours.push(cur.toISOString().split('T')[0]);
      cur.setDate(cur.getDate() + 1);
    }
    return jours;
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[460px] sm:w-[520px] overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2 text-lg">
              <Cloud className="w-5 h-5" />
              Projets — Jours de pluie
            </SheetTitle>
            <SheetDescription className="text-blue-100 text-sm">
              {(() => {
                // 🆕 2026-05-14 — Compteur filtré sur le mois affiché
                const d = currentDate || new Date();
                const moisStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                const moisEnd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-31`;
                const filtered = bonsPluie.filter(b => {
                  if (!b.pluie_date_debut || !b.pluie_date_fin) return false;
                  return !(b.pluie_date_fin < moisStart || b.pluie_date_debut > moisEnd);
                });
                const moisNom = d.toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' });
                return `${filtered.length} projet${filtered.length !== 1 ? 's' : ''} disponible${filtered.length !== 1 ? 's' : ''} en ${moisNom}`;
              })()}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="p-4 space-y-4">
          {/* Météo — prochains jours pluvieux */}
          {upcomingRainyDays.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-1">
                <Cloud className="w-4 h-4" />
                Prochains jours de pluie à &gt; 40 %
              </p>
              <p className="text-[11px] text-blue-700/80 mb-3">
                Seuls les jours avec ≥40 % de probabilité de précipitation sont affichés.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {upcomingRainyDays.map(([date, f]) => {
                  const d = new Date(date + 'T12:00:00');
                  const isToday = date === todayStr;
                  return (
                    <div key={date} className={`text-center p-2 rounded-lg border ${isToday ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-blue-200'}`}>
                      <p className={`text-xs font-medium ${isToday ? 'text-blue-100' : 'text-gray-500'}`}>
                        {isToday ? "Aujourd'hui" : d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-lg">{weatherEmoji(f.code)}</p>
                      <p className={`text-xs font-bold ${isToday ? 'text-white' : 'text-blue-700'}`}>{f.precip_pct}%</p>
                      <p className={`text-xs ${isToday ? 'text-blue-200' : 'text-gray-400'}`}>{Math.round(f.temp_max)}°</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Indicateurs pluie par équipe — TOUJOURS visible, même si 0h planifiées */}
          {(() => {
            const d = currentDate || new Date();
            const moisNom = d.toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' });
            return (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Couverture pluie par équipe — {moisNom}
                    {indTargetPct !== null && <span className="ml-1 text-blue-500 normal-case">(cible: {indTargetPct}%)</span>}
                  </p>
                  {indLoading && <span className="text-xs text-gray-400 animate-pulse">chargement...</span>}
                </div>
                {pluieIndicateurs.length === 0 && !indLoading ? (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Aucune équipe trouvée ou cibles non configurées. Vérifiez la section "Cibles et seuils".
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {pluieIndicateurs.map(ind => {
                      const isOpen = chefDeplie === ind.chef_id;
                      const projets = ind.projets || [];
                      return (
                      <div key={ind.chef_id} className={`rounded-lg border overflow-hidden ${
                        ind.statut === 'ok' ? 'bg-green-50 border-green-200' :
                        ind.statut === 'limite' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-red-50 border-red-200'
                      }`}>
                        <button
                          type="button"
                          onClick={() => setChefDeplie(isOpen ? null : ind.chef_id)}
                          className="w-full flex items-center gap-2 p-2.5 text-sm text-left hover:bg-white/40 transition-colors"
                          data-testid={`pluie-chef-row-${ind.chef_id}`}
                          title={projets.length > 0 ? `Cliquer pour voir les ${projets.length} projet(s) qui composent ces heures` : 'Aucun projet pluie ce mois'}
                        >
                          <ChevronRight
                            className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''} ${
                              ind.statut === 'ok' ? 'text-green-600' :
                              ind.statut === 'limite' ? 'text-yellow-600' :
                              'text-red-600'
                            }`}
                          />
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ind.couleur }} />
                          <span className="font-medium flex-1 truncate">
                            {ind.chef_nom}
                            {projets.length > 0 && (
                              <span className="ml-1.5 text-[10px] font-normal text-gray-500">({projets.length} projet{projets.length > 1 ? 's' : ''})</span>
                            )}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              ind.statut === 'ok' ? 'bg-green-100 text-green-700' :
                              ind.statut === 'limite' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {ind.statut === 'ok' ? '✓' : ind.statut === 'limite' ? '⚠' : '✗'} {ind.heures_pluie}h / {ind.cible_pluie}h
                            </span>
                            <span className={`text-xs font-semibold w-10 text-right ${
                              ind.statut === 'ok' ? 'text-green-600' : ind.statut === 'limite' ? 'text-yellow-600' : 'text-red-600'
                            }`}>{ind.pct}%</span>
                          </div>
                          {ind.statut === 'manque' && ind.ecart < 0 && (
                            <span className="text-xs text-red-500 font-medium">−{Math.abs(ind.ecart)}h</span>
                          )}
                        </button>
                        {/* 🆕 2026-05-18 — Décomposition par client */}
                        {isOpen && (
                          <div className="px-3 pb-2.5 pt-1 bg-white/60 border-t border-gray-200 space-y-1.5" data-testid={`pluie-projets-${ind.chef_id}`}>
                            {projets.length === 0 ? (
                              <p className="text-xs italic text-gray-500 py-2 text-center">
                                Aucun projet pluie attribué à {ind.chef_nom} pour ce mois.
                              </p>
                            ) : (
                              <>
                                <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide pt-1">
                                  Clients qui composent les {ind.heures_pluie}h pluie ce mois
                                </p>
                                {projets.map((proj, i) => {
                                  const nomAffiche = proj.client_nom || proj.nom_dossier || 'Client inconnu';
                                  const sousTitre = proj.client_nom && proj.nom_dossier && proj.client_nom !== proj.nom_dossier
                                    ? proj.nom_dossier
                                    : null;
                                  const pctChef = ind.heures_pluie > 0 ? Math.round((proj.heures_pluie_mois / ind.heures_pluie) * 100) : 0;
                                  return (
                                    <div
                                      key={proj.bon_id || i}
                                      className="bg-white rounded-md border border-gray-200 px-2.5 py-1.5 flex items-center gap-2 hover:border-blue-300 transition-colors"
                                      data-testid={`pluie-projet-${proj.bon_id}`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="text-xs font-semibold text-gray-900 truncate">{nomAffiche}</span>
                                          {proj.type_travaux && (
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                              proj.type_travaux === 'interieur' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                            }`}>
                                              {proj.type_travaux === 'interieur' ? 'Int.' : 'Ext.'}
                                            </span>
                                          )}
                                        </div>
                                        {sousTitre && <p className="text-[10px] text-gray-500 truncate">{sousTitre}</p>}
                                        {proj.pluie_date_debut && proj.pluie_date_fin && (
                                          <p className="text-[10px] text-blue-600/80 mt-0.5">
                                            <Cloud className="w-2.5 h-2.5 inline mr-0.5" />
                                            {proj.pluie_date_debut} → {proj.pluie_date_fin}
                                            {proj.jours_planifies_mois > 0 && (
                                              <span className="ml-1.5 text-gray-500">· {proj.jours_planifies_mois} j planifié{proj.jours_planifies_mois > 1 ? 's' : ''} ce mois</span>
                                            )}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right shrink-0">
                                        <p className="text-xs font-bold text-gray-900">{proj.heures_pluie_mois}h</p>
                                        <p className="text-[9px] text-gray-500">{pctChef}% du total</p>
                                      </div>
                                      {proj.dossier_id && (
                                        <a
                                          href={`/crm/projets/${proj.dossier_id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 hover:text-blue-800 text-[10px] underline shrink-0 ml-1"
                                          data-testid={`pluie-projet-voir-${proj.bon_id}`}
                                          onClick={(e) => e.stopPropagation()}
                                          title="Ouvrir le dossier dans un nouvel onglet"
                                        >
                                          Voir
                                        </a>
                                      )}
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Projets disponibles */}
          {bonsPluie.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Cloud className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-sm">Aucun projet marque "temps de pluie"</p>
              <p className="text-xs mt-1 text-gray-400">Cochez l'option dans le bon de travail pour ajouter des projets ici.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Planification rapide — mois affiché</p>
              {(() => {
                // 🆕 2026-05-14 — Filtrer les projets pour ne garder que ceux dont
                //   la plage de pluie chevauche le mois actuellement affiché dans
                //   le calendrier (currentDate). Les projets en dehors du mois
                //   sont relegués dans une section secondaire.
                const d = currentDate || new Date();
                const moisStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                const moisEnd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-31`;
                const matchesMonth = (bon) => {
                  if (!bon.pluie_date_debut || !bon.pluie_date_fin) return false;
                  return !(bon.pluie_date_fin < moisStart || bon.pluie_date_debut > moisEnd);
                };
                const enCeMois = bonsPluie.filter(matchesMonth);
                const horsMois = bonsPluie.filter(b => !matchesMonth(b));
                const renderBon = (bon) => {
                const hasPlage = !!(bon.pluie_date_debut && bon.pluie_date_fin);
                const plageDejaPlannifiee = isPlageAlreadyPlanned(bon);
                return (
                  <div key={bon.id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all">
                    {/* En-tête projet */}
                    <div className="px-4 py-3 bg-gray-50 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {bon.dossier_nom || bon.client_nom_display || `Bon ${bon.id?.slice(0, 8)}`}
                        </p>
                        {bon.client_nom_display && bon.dossier_nom && (
                          <p className="text-xs text-gray-500">{bon.client_nom_display}</p>
                        )}
                        {bon.chef_equipe_nom && (
                          <p className="text-xs text-gray-500 mt-0.5">Chef : {bon.chef_equipe_nom}</p>
                        )}
                        {/* Plage de dates pluie */}
                        {hasPlage && (
                          <div className="mt-1.5 flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                            <Cloud className="w-3 h-3 text-blue-500 shrink-0" />
                            <p className="text-xs text-blue-700 font-medium">
                              Entre le {formatDateFr(bon.pluie_date_debut)} et le {formatDateFr(bon.pluie_date_fin)}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 items-end ml-2">
                        <Badge className={bon.type_travaux === 'interieur' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-green-100 text-green-700 text-xs'}>
                          {bon.type_travaux === 'interieur' ? 'Intérieur' : 'Extérieur'}
                        </Badge>
                        {bon.heures_budgetees > 0 && (
                          <span className="text-xs text-gray-400">{bon.heures_budgetees}h budgétées</span>
                        )}
                      </div>
                    </div>

                    {/* Boutons de planification */}
                    <div className="px-4 py-3 space-y-2">
                      {hasPlage ? (
                        /* Bouton principal : Planifier toute la plage */
                        <div>
                          <button
                            disabled={planifyingId === `${bon.id}-plage` || plageDejaPlannifiee}
                            onClick={() => !plageDejaPlannifiee && handlePlanifier(bon, null)}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                              plageDejaPlannifiee
                                ? 'bg-green-50 text-green-700 border-green-200 cursor-default'
                                : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 cursor-pointer'
                            }`}
                            data-testid={`planifier-plage-${bon.id}`}
                          >
                            {planifyingId === `${bon.id}-plage` ? (
                              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                            ) : plageDejaPlannifiee ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <CalendarLucide className="w-4 h-4" />
                            )}
                            {plageDejaPlannifiee ? 'Plage planifiée au calendrier' : 'Planifier toute la plage'}
                          </button>
                          {plageDejaPlannifiee && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/deplanifier-pluie`, {});
                                  toast.success('Projet retire du calendrier');
                                  onRefresh();
                                } catch { toast.error('Erreur'); }
                              }}
                              className="w-full mt-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-all"
                              data-testid={`deplanifier-${bon.id}`}
                            >
                              <X className="w-3 h-3" /> Retirer du calendrier
                            </button>
                          )}
                          <p className="text-xs text-gray-400 mt-1 text-center">
                            Ajoute tous les jours ouvrables à votre calendrier de production
                          </p>
                          {/* Boutons rapides : aujourd'hui / demain */}
                          <div className="flex gap-2 mt-2">
                            {[todayStr, tomorrowStr].map(dateStr => {
                              const already = isAlreadyPlanned(bon, dateStr);
                              const label = dateStr === todayStr ? "Aujourd'hui seulement" : 'Demain seulement';
                              return (
                                <button
                                  key={dateStr}
                                  disabled={planifyingId === `${bon.id}-${dateStr}` || already}
                                  onClick={() => !already && handlePlanifier(bon, dateStr)}
                                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    already
                                      ? 'bg-green-50 text-green-700 border-green-200 cursor-default'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 hover:border-blue-400 cursor-pointer'
                                  }`}
                                >
                                  {already ? <CheckCircle className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                  {already ? 'Planifié' : label}
                                </button>
                              );
                            })}
                          </div>
                          {/* Sélecteur de dates multiples */}
                          <div className="mt-2">
                            {showDatePicker === bon.id ? (
                              <div className="border rounded-lg p-3 bg-white space-y-2">
                                <p className="text-xs font-semibold text-gray-700">Selectionnez les dates :</p>
                                <div className="grid grid-cols-4 gap-1 max-h-40 overflow-y-auto">
                                  {getPlageJours(bon).map(dateStr => {
                                    const isSelected = selectedMultiDates.includes(dateStr);
                                    const alreadyPlanned = (bon.dates_chantier || []).includes(dateStr);
                                    const d = new Date(dateStr + 'T12:00:00');
                                    return (
                                      <button
                                        key={dateStr}
                                        disabled={alreadyPlanned}
                                        onClick={() => toggleMultiDate(dateStr)}
                                        className={`p-1.5 rounded text-center text-xs border transition-all ${
                                          alreadyPlanned ? 'bg-green-50 text-green-600 border-green-200 cursor-default' :
                                          isSelected ? 'bg-blue-600 text-white border-blue-600' :
                                          'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
                                        }`}
                                      >
                                        <div className="font-bold">{d.getDate()}</div>
                                        <div className="text-[9px]">{d.toLocaleDateString('fr-CA', { weekday: 'short' })}</div>
                                      </button>
                                    );
                                  })}
                                </div>
                                {selectedMultiDates.length > 0 && (
                                  <p className="text-xs text-blue-600 font-medium">{selectedMultiDates.length} jour(s) selectionne(s)</p>
                                )}
                                <div className="flex gap-2">
                                  <button onClick={() => { setShowDatePicker(null); setSelectedMultiDates([]); }} className="flex-1 px-2 py-1.5 rounded text-xs border border-gray-300 hover:bg-gray-50">Annuler</button>
                                  <button
                                    disabled={selectedMultiDates.length === 0 || planifyingId === `${bon.id}-multi`}
                                    onClick={() => handlePlanifierMultiDates(bon)}
                                    className="flex-1 px-2 py-1.5 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {planifyingId === `${bon.id}-multi` ? 'Ajout...' : `Ajouter ${selectedMultiDates.length} jour(s)`}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setShowDatePicker(bon.id); setSelectedMultiDates([]); }}
                                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-blue-50 hover:border-blue-400 transition-all"
                              >
                                <CalendarLucide className="w-3 h-3" /> Choisir des dates specifiques
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* Pas de plage définie — affichage des jours pluvieux */
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Ajouter au calendrier :</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {[todayStr, tomorrowStr].map(dateStr => {
                              const already = isAlreadyPlanned(bon, dateStr);
                              const label = dateStr === todayStr ? "Aujourd'hui" : 'Demain';
                              return (
                                <button
                                  key={dateStr}
                                  disabled={planifyingId === `${bon.id}-${dateStr}` || already}
                                  onClick={() => !already && handlePlanifier(bon, dateStr)}
                                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    already
                                      ? 'bg-green-50 text-green-700 border-green-200 cursor-default'
                                      : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 cursor-pointer'
                                  }`}
                                  data-testid={`planifier-${dateStr === todayStr ? 'aujourd-hui' : 'demain'}-${bon.id}`}
                                >
                                  {already ? <CheckCircle className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                  {already ? 'Planifié' : label}
                                </button>
                              );
                            })}
                            {upcomingRainyDays.filter(([d]) => d !== todayStr && d !== tomorrowStr).slice(0, 3).map(([date, f]) => {
                              const already = isAlreadyPlanned(bon, date);
                              const d = new Date(date + 'T12:00:00');
                              return (
                                <button
                                  key={date}
                                  disabled={planifyingId === `${bon.id}-${date}` || already}
                                  onClick={() => !already && handlePlanifier(bon, date)}
                                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    already
                                      ? 'bg-green-50 text-green-700 border-green-200 cursor-default'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-400 cursor-pointer'
                                  }`}
                                >
                                  {already ? <CheckCircle className="w-3 h-3" /> : <span>{weatherEmoji(f.code)}</span>}
                                  {already ? 'Planifié' : d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric' })}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Lien vers le dossier */}
                      <button
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                        onClick={() => { navigate(`/crm/projets/${bon.dossier_id}?tab=bon-travail&bonId=${bon.id}`); onClose(); }}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Voir le dossier complet
                      </button>
                    </div>
                  </div>
                );
                };
                return (
                  <>
                    {enCeMois.map(renderBon)}
                    {horsMois.length > 0 && (
                      <details className="mt-3 border-t border-slate-200 pt-3">
                        <summary className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-slate-700">
                          🔽 Voir les {horsMois.length} projet(s) hors du mois affiché
                        </summary>
                        <div className="mt-3 space-y-3 opacity-75">
                          {horsMois.map(renderBon)}
                        </div>
                      </details>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default JoursPluieSheet;
